import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type AccountSecurityStatusResponse } from '@bliks-io/contracts';
import { Button, ConfirmDialog } from '@bliks-io/ui';
import { KeyRound, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCreatePasskeyRegistrationLink, useRemovePasskey } from '@/api/mutations/usePasskeys';
import type { PendingSensitiveAction, StepUpAction } from '@/lib/auth/stepUpReauth';

interface PasskeySectionProps {
  passkeys: AccountSecurityStatusResponse['passkeys'];
  pendingAction?: PendingSensitiveAction | null;
  /** Runs the action for a fresh session; a stale one opens the step-up dialog instead. */
  guardSensitive: (action: StepUpAction, run: () => void) => void;
  /** Opens SecurityTab's step-up dialog for the given interrupted action (403 backstop). */
  onStepUpRequired: (action: StepUpAction) => void;
}

/**
 * Passkey self-service: list of registered passkeys with removal, plus an
 * "Add passkey" action. Registration itself happens on Zitadel's hosted
 * login page (opened in a new tab) — the WebAuthn credential must be
 * created on the auth origin to be usable at sign-in. The security query
 * refetches on window focus, so the list updates when the user returns.
 */
export function PasskeySection({
  passkeys,
  pendingAction,
  guardSensitive,
  onStepUpRequired
}: PasskeySectionProps) {
  const { t } = useTranslation();
  const createRegistrationLink = useCreatePasskeyRegistrationLink({
    onReauthRequired: () => onStepUpRequired('passkey-add')
  });
  const removePasskey = useRemovePasskey({ onReauthRequired: onStepUpRequired });

  // After a step-up round trip the ConfirmDialog re-opens pre-targeted; the
  // destructive mutation still needs a fresh confirm click — persisted state
  // must never delete a credential on its own.
  const [passkeyToRemove, setPasskeyToRemove] = useState<string | null>(
    pendingAction?.kind === 'passkey-remove' ? pendingAction.passkeyId : null
  );

  // Ref (not isPending) guards double-clicks: the mutation state only
  // disables the button after a re-render, so a second click in the same
  // tick would mint a second one-time link and open a second blank tab.
  const registrationInFlightRef = useRef(false);

  const openRegistrationTab = async () => {
    if (registrationInFlightRef.current) return;
    registrationInFlightRef.current = true;
    // The tab must open synchronously inside the click gesture — opening it
    // after the await would be popup-blocked. No 'noopener' feature string
    // (it makes window.open return null in some browsers); sever the opener
    // reference manually instead.
    const tab = window.open('', '_blank');
    if (!tab) {
      toast.error(t('userSettings.security.passkeyPopupBlocked'));
      registrationInFlightRef.current = false;
      return;
    }
    tab.opener = null;
    try {
      const link = await createRegistrationLink.mutateAsync();
      tab.location.href = link.registrationUrl;
    } catch {
      // Error feedback (toast or step-up dialog) comes from the mutation's
      // onError; just don't leave a blank tab behind.
      tab.close();
    } finally {
      registrationInFlightRef.current = false;
    }
  };

  const onRemove = async () => {
    if (passkeyToRemove) {
      await removePasskey.mutateAsync(passkeyToRemove).catch(() => undefined);
    }
    setPasskeyToRemove(null);
  };

  return (
    <div className="space-y-4 border-t pt-6">
      <h3 className="text-sm font-medium">{t('userSettings.security.passkeysTitle')}</h3>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('userSettings.security.passkeysDescription')}
        </p>
        <Button
          type="button"
          onClick={() => guardSensitive('passkey-add', () => void openRegistrationTab())}
          loading={createRegistrationLink.isPending}
        >
          {t('userSettings.security.addPasskey')}
        </Button>
      </div>
      {passkeys.items.length > 0 && (
        <ul className="space-y-2">
          {passkeys.items.map((passkey) => (
            <li
              key={passkey.id}
              className="flex items-center justify-between gap-3 rounded-lg border p-3"
            >
              <div className="flex items-center gap-2 text-sm">
                <KeyRound className="h-5 w-5 text-primary" />
                <span>{passkey.name || t('userSettings.security.unnamedPasskey')}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('userSettings.security.removePasskey')}
                onClick={() =>
                  guardSensitive({ kind: 'passkey-remove', passkeyId: passkey.id }, () =>
                    setPasskeyToRemove(passkey.id)
                  )
                }
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={passkeyToRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPasskeyToRemove(null);
        }}
        icon={<Trash2 className="h-5 w-5" />}
        iconTone="danger"
        title={t('userSettings.security.removePasskey')}
        description={t('userSettings.security.removePasskeyConfirm')}
        confirmLabel={t('userSettings.security.removePasskey')}
        onConfirm={() => void onRemove()}
        variant="danger"
        isPending={removePasskey.isPending}
      />
    </div>
  );
}
